import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import api, { attachUnauthorizedHandler } from "../data/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [hydrated, setHydrated] = useState(false);

  // Probe the backend on mount: if the httpOnly session cookie is valid,
  // /api/auth/me returns the user; otherwise 401 → user stays null.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.me();
        if (!cancelled) setUser(data);
      } catch (_) {
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setHydrated(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // If any protected request 401s (session expired mid-session), clear local state.
  // ProtectedRoute will then redirect to /login.
  useEffect(() => {
    attachUnauthorizedHandler(() => setUser(null));
  }, []);

  const login = useCallback(async (usernameRaw, password) => {
    const username = (usernameRaw || "").trim();
    try {
      const { data } = await api.login(username, password);
      setUser(data.user);
      return data.user;
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        const e = new Error("Credenciales inválidas");
        e.code = "INVALID_CREDENTIALS";
        throw e;
      }
      if (status === 429) {
        const e = new Error("Demasiados intentos. Espera unos minutos.");
        e.code = "RATE_LIMITED";
        throw e;
      }
      const detail = err?.response?.data?.detail;
      throw new Error(typeof detail === "string" ? detail : "No se pudo iniciar sesión.");
    }
  }, []);

  const logout = useCallback(async () => {
    // Clear local state synchronously first so any concurrent navigation
    // (e.g. Sidebar's navigate to /login) sees us as unauthenticated.
    setUser(null);
    try {
      await api.logout();
    } catch (_) {
      // best-effort: cookie clears server-side, local state already dropped
    }
  }, []);

  const value = {
    user,
    isAuthenticated: !!user,
    hydrated,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}
